import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';

import 'grapesjs/dist/css/grapes.min.css';
import grapesJS from 'grapesjs';
import pluginMJML from 'grapesjs-mjml';
import pluginScribeJs from 'grapesjs-plugin-scribejs';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { ValidationException } from '../../exceptions/validation-exception';
import { MatDialogRef } from '@angular/material/dialog';
import { SpinnerDialogComponent } from '../dialogs/spinner-dialog/spinner-dialog.component';
import { SpinnerService } from 'src/app/services/spinner.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ResponsiveBaseComponent } from '../responsive-base/responsive-base.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CreateNewsletterTemplateComponentStore } from 'src/app/stores/components/newsletter-template-create/create-newsletter-template.component-store';
import { CreateNewsletterTemplateModel } from 'src/app/models/create-newsletter-template-model';

declare const grapesjs: any;

@Component({
  selector: 'app-newsletter-template-create',
  templateUrl: './newsletter-template-create.component.html',
  styleUrl: './newsletter-template-create.component.scss',
  standalone: false,
})
export class NewsletterTemplateCreateComponent extends ResponsiveBaseComponent implements OnInit {
  public readonly minNameLength: number = 1;
  public readonly maxNameLength: number = 32;
  public newsletterTemplateCreateFormGroup: FormGroup;

  @ViewChild('gjs', { static: true })
  private gjs: ElementRef;
  private editor: any;

  private readonly ngUnsubscribeState = new Subject<void>();
  private loadingSpinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;

  private companyId: string;

  constructor(
    responsive: BreakpointObserver,
    private readonly createNewsletterTemplateComponentStore: CreateNewsletterTemplateComponentStore,
    private readonly spinnerService: SpinnerService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
  ) {
    super(responsive);
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.companyId = params.companyId;
    });

    this.initializeEditor();
    
    this.createNewsletterTemplateComponentStore.errorMessage$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((errorMessage) => {
      this.hideSpinner(true);
      if (errorMessage !== null && errorMessage !== undefined) {
        if (errorMessage instanceof ValidationException) {
          const validationViolationMessage = errorMessage.violations.map(v => v.field + ' ' + v.message).join("\n");
          this.snackBar.open(validationViolationMessage, "Dismiss");
        }
        else {
          this.snackBar.open(errorMessage, "Dismiss");
        }
      }
    });

    this.createNewsletterTemplateComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });

    this.createNewsletterTemplateComponentStore.success$.pipe(takeUntil(this.ngUnsubscribeState), filter(success => Boolean(success))).subscribe(() => {
      this.router.navigate(['/companies/', this.companyId, 'newsletters', 'templates']);
      this.snackBar.open("Newsletter Template created successfully", "Dismiss");
    });

    this.newsletterTemplateCreateFormGroup = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(this.minNameLength), Validators.maxLength(this.maxNameLength)]],
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribeState.next();
    this.ngUnsubscribeState.complete();
    this.clearEditor();
  }

  initializeEditor(): void {
    this.gjs.nativeElement.innerHTML = '<mjml><mj-body><mj-section><mj-column></mj-column></mj-section></mj-body></mjml>';
    this.editor = grapesJS.init({
      container: '#gjs',
      height: '70%',

      fromElement: true,
      plugins: [
        pluginScribeJs,
        pluginMJML,
        'grapesjs-plugin-borders',
      ],
      pluginsOpts: {
        'grapesjs-plugin-borders': {
          'main_color': '#d96caa', // For all
          // OR
          // 'selected': '#2ca4e0',
          // 'resizer': '#FF0000',
          // 'toolbar': '#FF0000',
          // 'badge': '#DD0000',
          // 'highlighter': '#FF0000'
        }
      },
      style: '.txt-red{color: #d96caa}',
      storageManager: false,
    });

    this.addSaveButton();
    this.saveNewsletterTemplate();
  }

  clearEditor() : void {
    this.editor.destroy();
  }

  addSaveButton(): void {
    this.editor.Panels.addButton('options', {
      id: ' save-button',
      className: 'fa fa-floppy-o',
      command: 'save-newsletter-template',
      attributes: { title: 'Save Newsletter Template' }
    });
  }

  saveNewsletterTemplate(): void {
    this.editor.Commands.add('save-newsletter-template', {
      run: (editor: any) => {
        this.newsletterTemplateCreateFormGroup.markAllAsTouched();
        if (this.newsletterTemplateCreateFormGroup.valid) {

          const templateContent = editor.runCommand('mjml-code-to-html')?.html;
          const htmlContent = editor.getHtml();
          const cssContent = editor.getCss();
          const projectData = editor.getProjectData();

          this.showSpinner();
          const createNewsletterTemplate = new CreateNewsletterTemplateModel();

          createNewsletterTemplate.name = this.newsletterTemplateCreateFormGroup.value.name;
          createNewsletterTemplate.htmlContent = htmlContent;
          createNewsletterTemplate.cssContent = cssContent;
          createNewsletterTemplate.templateContent = templateContent;
          createNewsletterTemplate.projectData = projectData;
          this.createNewsletterTemplateComponentStore.createNewsletterTemplate({ companyId: this.companyId, createNewsletterTemplate: createNewsletterTemplate });
        }
      }
    });
  }

  private showSpinner() {
    this.loadingSpinnerDialogRef = this.spinnerService.show();
  }

  private hideSpinner(loaded: boolean) {
    if (loaded && this.loadingSpinnerDialogRef !== null) {
      this.spinnerService.hide(this.loadingSpinnerDialogRef);
      this.loadingSpinnerDialogRef = null;
    }
  }
}